import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MessageSquare, Eye, EyeOff, Loader2, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { AUTH } from "@/constants/testIds";

const FEATURES = [
  "99.2% delivery rate across all networks",
  "Real-time delivery reports",
  "Developer-friendly REST API",
  "Enterprise-grade security",
];

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [form, setForm] = useState({ email: "", password: "" });
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await login(form.email, form.password);
      toast.success("Welcome back!");
      if (user.role === "master_admin") navigate("/admin");
      else navigate("/dashboard");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFC] flex">
      {/* Left panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-[#0c1e41] to-[#060e1c] flex-col justify-between p-12">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center">
            <MessageSquare size={16} className="text-white" />
          </div>
          <span className="text-xl font-extrabold text-white">Sendora</span>
        </div>
        <div>
          <h2 className="text-3xl font-extrabold text-white mb-4 leading-tight">
            Nigeria's Premier<br />SMS Platform
          </h2>
          <p className="text-[#adc0e0] text-sm leading-relaxed mb-8">
            Send bulk SMS, manage campaigns, track delivery rates and scale your communication infrastructure across all Nigerian networks.
          </p>
          <div className="space-y-3">
            {FEATURES.map((f) => (
              <div key={f} className="flex items-center gap-3 text-sm text-[#dce4f0]">
                <div className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center shrink-0">
                  <div className="w-2 h-2 rounded-full bg-white" />
                </div>
                {f}
              </div>
            ))}
          </div>
        </div>
        <p className="text-xs text-[#7a97c9]">© 2026 Sendora.</p>
      </div>

      {/* Right panel */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-2 mb-8 justify-center">
            <div className="w-8 h-8 rounded-lg bg-[#0c1e41] flex items-center justify-center">
              <MessageSquare size={16} className="text-white" />
            </div>
            <span className="text-xl font-extrabold text-slate-900">Sendora</span>
          </div>

          <div className="mb-8">
            <h1 className="text-2xl font-extrabold text-slate-900 mb-2">Sign in to your account</h1>
            <p className="text-slate-500 text-sm">
              Don't have an account?{" "}
              <Link to="/register" className="text-[#0c1e41] hover:text-[#0c1e41] font-semibold">
                Create one free
              </Link>
            </p>
          </div>

          <form data-testid={AUTH.loginForm} onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-lg">
                {error}
              </div>
            )}

            <div className="space-y-2">
              <Label className="text-slate-700 text-sm font-medium">Email Address</Label>
              <Input
                data-testid={AUTH.loginEmail}
                type="email"
                placeholder="you@company.com"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
                className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-400 h-11 focus:border-[#0c1e41] focus:ring-1 focus:ring-[#0c1e41]/20"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-slate-700 text-sm font-medium">Password</Label>
              <div className="relative">
                <Input
                  data-testid={AUTH.loginPassword}
                  type={showPass ? "text" : "password"}
                  placeholder="Enter your password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  required
                  className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-400 h-11 focus:border-[#0c1e41] focus:ring-1 focus:ring-[#0c1e41]/20 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPass(!showPass)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <Button
              data-testid={AUTH.loginSubmit}
              type="submit"
              disabled={loading}
              className="w-full h-11 bg-[#0c1e41] hover:bg-[#091629] text-white font-semibold shadow-sm"
            >
              {loading ? (
                <><Loader2 size={16} className="mr-2 animate-spin" />Signing in...</>
              ) : (
                <>Sign In <ArrowRight size={15} className="ml-2" /></>
              )}
            </Button>
          </form>

          <p className="mt-8 text-center text-xs text-slate-400">
            By signing in, you agree to our{" "}
            <a href="#" className="text-slate-500 hover:text-[#0c1e41]">Terms of Service</a>
            {" "}and{" "}
            <a href="#" className="text-slate-500 hover:text-[#0c1e41]">Privacy Policy</a>
          </p>
        </div>
      </div>
    </div>
  );
}
